import React, { useState } from 'react';
import { DollarSign, Briefcase, Calendar, CheckCircle, Shield, Lock } from 'lucide-react';

const PreLander = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});

  const questions = [
    {
      id: 1,
      question: "How much do you need?",
      subtitle: "Choose your loan amount",
      icon: <DollarSign className="w-7 h-7" />,
      options: [
        { value: "1000", label: "$1,000" },
        { value: "2500", label: "$2,500" },
        { value: "5000", label: "$5,000" },
        { value: "7500", label: "$7,500" }
      ]
    },
    {
      id: 2,
      question: "What's your employment status?",
      subtitle: "All income types accepted",
      icon: <Briefcase className="w-7 h-7" />,
      options: [
        { value: "employed_fulltime", label: "Employed Full-Time" },
        { value: "employed_parttime", label: "Employed Part-Time" },
        { value: "self_employed", label: "Self-Employed" },
        { value: "benefits", label: "Benefits / Other Income" }
      ]
    },
    {
      id: 3,
      question: "How often do you get paid?",
      subtitle: "Select your pay schedule",
      icon: <Calendar className="w-7 h-7" />,
      options: [
        { value: "weekly", label: "Weekly" },
        { value: "biweekly", label: "Every 2 Weeks" },
        { value: "monthly", label: "Monthly" },
        { value: "other", label: "Other" }
      ]
    }
  ];

  const currentQuestion = questions[step - 1];
  const progress = (step / questions.length) * 100;

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);

    setTimeout(() => {
      if (step < questions.length) {
        setStep(step + 1);
      } else {
        onComplete(newAnswers);
      }
    }, 300);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      
      {/* Top Bar */}
      <div className="bg-white border-b border-slate-200 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-700 text-sm font-medium">
            <Lock className="w-4 h-4 text-emerald-600" />
            <span>Secure Application</span>
          </div>
          <div className="flex items-center gap-2 text-slate-700 text-sm font-medium">
            <Shield className="w-4 h-4 text-emerald-600" />
            <span>SSL Encrypted</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-2xl">
          
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-900 text-sm font-semibold">Question {step} of {questions.length}</span>
              <span className="text-emerald-600 text-sm font-semibold">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
            
            {/* Header */}
            <div className="bg-slate-50 border-b border-slate-200 px-6 sm:px-8 py-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-50 border border-emerald-200 rounded-2xl mb-5">
                <div className="text-emerald-600">
                  {currentQuestion.icon}
                </div>
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight mb-2">
                {currentQuestion.question}
              </h2>
              <p className="text-base text-slate-600">
                {currentQuestion.subtitle}
              </p>
            </div>

            {/* Options */}
            <div className="p-6 sm:p-8 space-y-3">
              {currentQuestion.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  className="w-full bg-white hover:bg-slate-50 border-2 border-slate-200 hover:border-emerald-500 rounded-xl px-6 py-5 text-left transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium text-slate-900 group-hover:text-emerald-600 transition-colors">
                      {option.label}
                    </span>
                    <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-emerald-600 flex items-center justify-center transition-colors">
                      <svg className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
              <div className="flex items-center justify-center gap-6 flex-wrap text-xs text-slate-600">
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">100% Secure</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">No Credit Check</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">Fast Approval</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreLander;
